import { Box, Typography, Chip, Button } from '@mui/material';
import { vortxColors, vortxTypography, chartColors } from '@vortx/shared-ui';
import AnimatedMetric from '../AnimatedMetric';
import StepSlide, { SplitView, DemoBullets } from '../StepSlide';
import { retroColors, demoColors } from '../theme';
import {
    SmartToyOutlined,
    PeopleOutlined,
    CodeOutlined,
    LayersOutlined,
    OpenInNewOutlined,
    CalendarTodayOutlined,
} from '@mui/icons-material';

/**
 * Step 9: "AI-Native Operations — 7 People, 70+ MW"
 */
export function Step9_AINative({ visible }) {
    const layers = [
        { label: 'Intelligence', items: 'AI extraction, tariff agents, geocoding, utility screening', color: vortxColors.gold },
        { label: 'Design', items: 'Site DNA, BESS sizing, interconnection, revenue stacks', color: demoColors.actPlatform },
        { label: 'Optimization', items: 'Julia solver, HiGHS LP/MILP, ADMM coordination', color: chartColors.hvac },
        { label: 'Operations', items: 'Flux EMS, Gateway (BACnet/Modbus/OCPP), Digital Twin', color: chartColors.ev },
        { label: 'Enterprise', items: 'Multi-tenancy, accounts, investor reporting, billing', color: retroColors.muted },
    ];

    return (
        <StepSlide
            title="AI-Native Operations"
            subtitle="7 people managing 70+ MW. A traditional infrastructure company would need 35."
            keyStat="Revenue per employee: $490K+. The software scales. The team does not need to."
            visible={visible}
        >
            <SplitView
                left={
                    <Box>
                        {/* Comparison */}
                        <Box sx={{
                            display: 'grid',
                            gridTemplateColumns: '1fr 1fr',
                            gap: 2,
                            mb: 3,
                        }}>
                            <Box sx={{ p: 2, borderRadius: 2, bgcolor: 'rgba(231, 76, 60, 0.05)', border: '1px solid rgba(231, 76, 60, 0.15)', textAlign: 'center' }}>
                                <PeopleOutlined sx={{ color: vortxColors.error, mb: 1 }} />
                                <Typography sx={{ fontFamily: vortxTypography.mono, fontWeight: 700, fontSize: '2rem', color: vortxColors.error }}>35</Typography>
                                <Typography sx={{ fontFamily: vortxTypography.body, fontSize: '0.75rem', color: retroColors.muted }}>Traditional team for 70 MW</Typography>
                            </Box>
                            <Box sx={{ p: 2, borderRadius: 2, bgcolor: 'rgba(95, 212, 196, 0.05)', border: '1px solid rgba(95, 212, 196, 0.15)', textAlign: 'center' }}>
                                <SmartToyOutlined sx={{ color: demoColors.actPlatform, mb: 1 }} />
                                <Typography sx={{ fontFamily: vortxTypography.mono, fontWeight: 700, fontSize: '2rem', color: demoColors.actPlatform }}>7</Typography>
                                <Typography sx={{ fontFamily: vortxTypography.body, fontSize: '0.75rem', color: retroColors.muted }}>Vortx team for 70+ MW</Typography>
                            </Box>
                        </Box>

                        {/* How AI works */}
                        <Typography sx={{ fontFamily: vortxTypography.heading, fontWeight: 600, fontSize: '0.9rem', color: vortxColors.white, mb: 1.5 }}>
                            AI handles execution. Humans review and decide.
                        </Typography>
                        <DemoBullets items={[
                            'Site DNA automates geospatial analysis + utility screening',
                            'Flux EMS dispatches autonomously — 35,040 decisions/yr/site',
                            'AI-assisted development: Claude Code writes software, models, reports',
                            'Grid services: automated enrollment + compliance + settlement',
                        ]} />
                    </Box>
                }
                right={
                    <Box>
                        {/* Platform layers */}
                        <Typography sx={{
                            fontFamily: vortxTypography.mono,
                            fontSize: '0.65rem',
                            color: retroColors.muted,
                            textTransform: 'uppercase',
                            letterSpacing: '0.15em',
                            mb: 1.5,
                        }}>
                            Platform Architecture
                        </Typography>
                        {layers.map((layer, i) => (
                            <Box key={i} sx={{
                                display: 'flex',
                                gap: 1.5,
                                alignItems: 'center',
                                p: 1.5,
                                mb: 0.5,
                                borderRadius: 1,
                                bgcolor: retroColors.surface,
                                borderLeft: `3px solid ${layer.color}`,
                            }}>
                                <Chip
                                    label={`L${i + 1}`}
                                    size="small"
                                    sx={{
                                        bgcolor: `${layer.color}20`,
                                        color: layer.color,
                                        fontFamily: vortxTypography.mono,
                                        fontSize: '0.6rem',
                                        fontWeight: 700,
                                        minWidth: 32,
                                    }}
                                />
                                <Box>
                                    <Typography sx={{ fontFamily: vortxTypography.body, fontWeight: 600, fontSize: '0.8rem', color: vortxColors.white }}>
                                        {layer.label}
                                    </Typography>
                                    <Typography sx={{ fontFamily: vortxTypography.mono, fontSize: '0.7rem', color: retroColors.muted }}>
                                        {layer.items}
                                    </Typography>
                                </Box>
                            </Box>
                        ))}

                        <Box sx={{ mt: 2, textAlign: 'center' }}>
                            <Typography sx={{
                                fontFamily: vortxTypography.mono,
                                fontSize: '0.75rem',
                                color: retroColors.dimmed,
                            }}>
                                15+ microservices · Julia + Python + React · 7,100+ commits · Built by 2 people
                            </Typography>
                        </Box>
                    </Box>
                }
            />
        </StepSlide>
    );
}

/**
 * Step 10: "The Ask"
 */
export function Step10_TheAsk({ visible, onTryPlatform, onSchedule }) {
    return (
        <StepSlide
            title="The Ask"
            visible={visible}
        >
            {/* Unit economics */}
            <Box sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' },
                gap: 2,
                mb: 4,
            }}>
                <Box sx={{ textAlign: 'center' }}>
                    <AnimatedMetric value={200} prefix="$" suffix="K" label="Dev Fee / Site" />
                </Box>
                <Box sx={{ textAlign: 'center' }}>
                    <AnimatedMetric value={3} prefix="$" suffix="/kW-mo" label="Flux-as-a-Service" decimals={2} />
                </Box>
                <Box sx={{ textAlign: 'center' }}>
                    <AnimatedMetric value={96} suffix="%" label="Recurring Gross Margin" />
                </Box>
                <Box sx={{ textAlign: 'center' }}>
                    <AnimatedMetric value={470} prefix="$" suffix="K" label="ARR per Site" />
                </Box>
            </Box>

            {/* Scale trajectory */}
            <Box sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' },
                gap: 2,
                mb: 4,
            }}>
                {[
                    { year: 'Year 1', sites: '2 sites', revenue: '$200K+', team: 'Team of 3', ebitda: '' },
                    { year: 'Year 2', sites: '6 sites', revenue: 'Flux recurring', team: 'Team of 5', ebitda: '' },
                    { year: 'Year 3', sites: '70 MW', revenue: '$3M ARR', team: 'Team of 7', ebitda: '$2M EBITDA' },
                ].map((row, i) => (
                    <Box key={i} sx={{
                        p: 2.5,
                        borderRadius: 2,
                        bgcolor: retroColors.surface,
                        border: `1px solid rgba(178, 140, 80, ${i === 2 ? '0.3' : '0.1'})`,
                        textAlign: 'center',
                    }}>
                        <Chip
                            label={row.year}
                            size="small"
                            sx={{
                                bgcolor: i === 2 ? vortxColors.gold : 'rgba(136,136,136,0.1)',
                                color: i === 2 ? retroColors.bg : retroColors.muted,
                                fontFamily: vortxTypography.mono,
                                fontSize: '0.65rem',
                                fontWeight: 700,
                                mb: 1.5,
                            }}
                        />
                        <Typography sx={{ fontFamily: vortxTypography.mono, fontWeight: 700, fontSize: '1.3rem', color: vortxColors.white }}>
                            {row.sites}
                        </Typography>
                        <Typography sx={{ fontFamily: vortxTypography.mono, fontSize: '0.8rem', color: demoColors.actPlatform }}>
                            {row.revenue}
                        </Typography>
                        <Typography sx={{ fontFamily: vortxTypography.body, fontSize: '0.75rem', color: retroColors.muted }}>
                            {row.team}
                        </Typography>
                        {row.ebitda && (
                            <Typography sx={{ fontFamily: vortxTypography.mono, fontSize: '0.85rem', color: vortxColors.gold, fontWeight: 600, mt: 0.5 }}>
                                {row.ebitda}
                            </Typography>
                        )}
                    </Box>
                ))}
            </Box>

            {/* Macro thesis */}
            <Box sx={{
                p: 3,
                borderRadius: 2,
                bgcolor: 'rgba(178, 140, 80, 0.05)',
                border: `1px solid rgba(178, 140, 80, 0.15)`,
                mb: 4,
            }}>
                <DemoBullets items={[
                    'Distributed energy is the fastest-growing segment of the grid.',
                    'Every distributed site needs intelligence and control. That\'s what Vortx provides.',
                    'Our beachhead — powered land sites for AI infrastructure — is the most urgent use case.',
                    'We\'re building the intelligence layer at the center of distributed energy growth.',
                ]} />
            </Box>

            {/* CTAs */}
            <Box sx={{
                display: 'flex',
                gap: 2,
                justifyContent: 'center',
                flexWrap: 'wrap',
            }}>
                {onTryPlatform && (
                    <Button
                        variant="contained"
                        size="large"
                        endIcon={<OpenInNewOutlined />}
                        onClick={onTryPlatform}
                        sx={{
                            bgcolor: vortxColors.gold,
                            color: retroColors.bg,
                            fontFamily: vortxTypography.heading,
                            fontWeight: 700,
                            px: 4,
                            '&:hover': { bgcolor: vortxColors.goldHover },
                        }}
                    >
                        Try the Platform
                    </Button>
                )}
                {onSchedule && (
                    <Button
                        variant="outlined"
                        size="large"
                        endIcon={<CalendarTodayOutlined />}
                        onClick={onSchedule}
                        sx={{
                            borderColor: vortxColors.gold,
                            color: vortxColors.gold,
                            fontFamily: vortxTypography.heading,
                            fontWeight: 700,
                            px: 4,
                            '&:hover': {
                                borderColor: vortxColors.goldHover,
                                bgcolor: 'rgba(178, 140, 80, 0.1)',
                            },
                        }}
                    >
                        Schedule Follow-up
                    </Button>
                )}
            </Box>
        </StepSlide>
    );
}
